import { Component, OnInit } from "@angular/core";
import { UserRole } from "./user-admins.interface";
import { UserAdminsService } from "./user-admins.service";
import { MatTableDataSource } from "@angular/material/table";
import { AuthService } from "../auth/auth.service";
import { ConfirmationDialogComponent } from "../shared/confirmation-dialog/confirmation-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { ToastrTranslatedService } from "../shared/toastr-translated.service";
import { UserAdminsEditDialogComponent } from "./user-admins-edit-dialog/user-admins-edit-dialog.component";
import { UserAdminsCreateDialogComponent } from "./user-admins-create-dialog/user-admins-create-dialog.component";

@Component({
  selector: "app-user-admins",
  templateUrl: "./user-admins.component.html",
  styleUrls: ["./user-admins.component.scss"],
  standalone: false,
})
export class UserAdminsComponent implements OnInit {
  public displayedColumns = [
    "email",
    "role",
    "createdAt",
    "activityTime",
    "actions",
  ];
  public dataSource = new MatTableDataSource<UserRole>();
  public isLoading = true;

  constructor(
    private readonly service: UserAdminsService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private toastr: ToastrTranslatedService,
    public auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.fetchAdmins();
  }

  fetchAdmins = () => {
    this.isLoading = true;
    this.service.fetchAdmins().subscribe({
      next: (admins) => {
        this.dataSource.data = admins;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  };

  removeAdmin = (userRoleId: string) => {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant(
            "userAdmins.deleteConfirmationText",
          ) as string,
        },
      })
      .afterClosed()
      .subscribe(async (confirm: boolean) => {
        if (confirm) {
          await this.service
            .removeAdmin(userRoleId)
            .toPromise()
            .then(() => {
              this.fetchAdmins();
              this.toastr.success(
                this.translate.instant("userAdmins.deleteSuccess") as string,
              );
            });
        }
      });
  };

  editAdmin = (userRole: UserRole) => {
    this.dialog
      .open(UserAdminsEditDialogComponent, {
        data: userRole,
        panelClass: "app-dialog-panel",
      })
      .afterClosed()
      .subscribe((response: { email: string; role: string } | undefined) => {
        if (response) {
          userRole.email = response.email;
          userRole.role = response.role as UserRole["role"];
          this.toastr.success(
            this.translate.instant("userAdmins.changeSuccess") as string,
          );
        }
      });
  };

  canManageAdmin = (userRole: UserRole): boolean =>
    this.auth.isSuperAdmin ||
    userRole.role === "COMPANY_MANAGER" ||
    (this.auth.isGroupManager && userRole.role === "GROUP_MANAGER") ||
    (this.auth.isReseller && userRole.role === "RESELLER") ||
    (this.auth.isReseller && userRole.role !== "SUPER_ADMIN");

  onRowClick = (userRole: UserRole): void => {
    if (this.canManageAdmin(userRole)) {
      this.editAdmin(userRole);
    }
  };

  addAdmin = () => {
    this.dialog
      .open(UserAdminsCreateDialogComponent, {
        panelClass: "app-dialog-panel",
      })
      .afterClosed()
      .subscribe((response: unknown) => {
        if (response) {
          this.fetchAdmins();
          this.toastr.success(
            this.translate.instant("userAdmins.addSuccess") as string,
          );
        }
      });
  };
}
